/**
 * Helpers d'export Excel (XLSX) et PDF — réutilisables pour toutes les pages.
 * Les libs jspdf/xlsx sont chargées en dynamic import pour ne pas alourdir
 * le bundle initial (chunk séparé Vite).
 */

export interface ExportColumn<T> {
  header:   string;
  accessor: (row: T) => string | number;
  align?:   'left' | 'right' | 'center';
  width?:   number;  // pour PDF (en mm) ; pour Excel, calculé auto
}

export interface ExportOptions<T> {
  filename:   string;
  columns:    ExportColumn<T>[];
  rows:       T[];
}

export interface ExportXLSXOptions<T> extends ExportOptions<T> {
  sheetName?: string;
}

export interface ExportPDFOptions<T> extends ExportOptions<T> {
  title:        string;
  subtitle?:    string;
  orientation?: 'portrait' | 'landscape';
  /** Couleur RGB du header (par défaut vert Leadbeton). */
  headerColor?: [number, number, number];
}

const fmtDateForFilename = () => new Date().toISOString().slice(0, 10);

/** Génère un nom de fichier propre : `<base>_<filialeKey>_<YYYY-MM-DD>.<ext>` */
export const buildExportFilename = (base: string, filialeKey: string | undefined, ext: 'xlsx' | 'pdf' | 'csv'): string => {
  const filiale = filialeKey ? `_${filialeKey}` : '';
  return `${base}${filiale}_${fmtDateForFilename()}.${ext}`;
};

export async function exportToXLSX<T>(opts: ExportXLSXOptions<T>): Promise<void> {
  const xlsx = await import('xlsx');
  const data: (string | number)[][] = [
    opts.columns.map((c) => c.header),
    ...opts.rows.map((row) => opts.columns.map((c) => c.accessor(row))),
  ];
  const ws = xlsx.utils.aoa_to_sheet(data);

  // Largeurs auto basées sur le max des cellules de la colonne
  ws['!cols'] = opts.columns.map((col, i) => {
    const headerLen = String(col.header).length;
    const maxBodyLen = data.slice(1).reduce((m, row) => Math.max(m, String(row[i] ?? '').length), 0);
    return { wch: Math.min(60, Math.max(headerLen, maxBodyLen) + 2) };
  });

  const wb = xlsx.utils.book_new();
  xlsx.utils.book_append_sheet(wb, ws, opts.sheetName ?? 'Export');
  xlsx.writeFile(wb, opts.filename);
}

export async function exportToPDF<T>(opts: ExportPDFOptions<T>): Promise<void> {
  const { jsPDF } = await import('jspdf');
  const { default: autoTable } = await import('jspdf-autotable');

  const doc = new jsPDF({ orientation: opts.orientation ?? 'landscape' });
  const headerColor = opts.headerColor ?? [31, 132, 92]; // Vert Leadbeton

  // Header
  doc.setFontSize(16);
  doc.setFont('helvetica', 'bold');
  doc.text(opts.title, 14, 18);
  if (opts.subtitle) {
    doc.setFontSize(10);
    doc.setFont('helvetica', 'normal');
    doc.setTextColor(100);
    doc.text(opts.subtitle, 14, 25);
  }
  doc.setFontSize(8);
  doc.setTextColor(150);
  doc.text(`Généré le ${new Date().toLocaleString('fr-FR')} — SIM GROUP Holding Platform`, 14, 31);

  // Table
  autoTable(doc, {
    head:    [opts.columns.map((c) => c.header)],
    body:    opts.rows.map((row) => opts.columns.map((c) => String(c.accessor(row)))),
    startY:  36,
    margin:  { left: 14, right: 14 },
    styles:  { fontSize: 8, cellPadding: 2 },
    headStyles: { fillColor: headerColor, textColor: 255, fontStyle: 'bold' },
    alternateRowStyles: { fillColor: [248, 248, 248] },
    columnStyles: opts.columns.reduce((acc, c, i) => {
      const style: { halign?: 'left' | 'right' | 'center'; cellWidth?: number } = {};
      if (c.align) style.halign = c.align;
      if (c.width) style.cellWidth = c.width;
      if (Object.keys(style).length > 0) acc[i] = style;
      return acc;
    }, {} as Record<number, { halign?: 'left' | 'right' | 'center'; cellWidth?: number }>),
    didDrawPage: (data) => {
      // Footer avec numéro de page
      const pageHeight = doc.internal.pageSize.height;
      doc.setFontSize(7);
      doc.setTextColor(150);
      doc.text(
        `Page ${(doc as unknown as { internal: { getNumberOfPages: () => number } }).internal.getNumberOfPages()}`,
        data.settings.margin.left,
        pageHeight - 8,
      );
    },
  });

  doc.save(opts.filename);
}
